import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  Field,
  Input,
  makeStyles,
  Textarea,
} from "@fluentui/react-components";
import {
  Attach20Regular,
  Dismiss24Regular,
  Send24Filled,
} from "@fluentui/react-icons";
import React, { useRef, useState } from "react";
import FormikInputRequest from "../../../../service/hook/useFormik";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../../redux/store";
import {
  fetchCustomerMessages,
  postMessage,
} from "../../../../redux/clientPortal/clientPortalslice";
import DynamicDropzone from "../../../../components/DynamicDropzone/Dropzone";

const ALLOWED_FILE_TYPES = [
  "image/jpeg",
  "image/png",
  "image/gif",
  "application/pdf",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "text/plain",
  "text/csv",
];

const ALLOWED_FILE_EXTENSIONS = ".jpg,.jpeg,.png,.gif,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv";

const useStyles = makeStyles({
  dialogContent: {
    padding: "10px",
    borderRadius: "4px",
  },
});

function ComposeMessage({ open, onClose, customerId, notify, userId }: any) {
  const styles = useStyles();
  const dispatch = useDispatch<AppDispatch>();
  const [actionProcessing, setActionProcessing] = useState(false);
  const [rawFiles, setRawFiles] = useState<File[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { loading } = useSelector((state: RootState) => state.fileUpload);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files;
    if (selected && selected.length > 0) {
      const validFiles = Array.from(selected).filter((f) =>
        ALLOWED_FILE_TYPES.includes(f.type),
      );
      const invalidCount = selected.length - validFiles.length;
      if (invalidCount > 0) {
        notify(
          `${invalidCount} file(s) were rejected. Allowed types: PDF, Word, Excel, images, text.`,
          "",
          "warning",
        );
      }
      if (validFiles.length > 0) {
        setRawFiles((prev) => [...prev, ...validFiles]);
      }
    }
    // Reset input so the same file can be re-selected if removed
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const formikData = FormikInputRequest({
    initialValues: {
      subject: "",
      message: "",
    },
    validate: (values) => {
      const errors: any = {};
      if (!values.subject) {
        errors.subject = "Subject is required";
      } else if (values.subject.length < 5) {
        errors.subject = "Subject must be at least 5 characters long";
      } else if (values.subject.length > 150) {
        errors.subject = "Subject must not exceed 150 characters";
      }
      if (values.message.length > 5000) {
        errors.message = "Message must not exceed 5000 characters";
      }
      return errors;
    },
    onSubmitFn: async (values: any) => {
      const scanningFiles = uploadedFiles.filter(
        (f) => f.status === "uploading" || f.status === "scanning",
      );
      if (scanningFiles.length > 0) {
        notify("Please wait for file scanning to complete", "", "warning");
        return;
      }

      const failedFiles = uploadedFiles.filter((f) => f.status === "error");
      if (failedFiles.length > 0) {
        notify(
          "One or more files failed security validation. Please remove them before sending.",
          "",
          "error",
        );
        return;
      }

      setActionProcessing(true);
      const { subject, message } = values;
      const fileToSend =
        uploadedFiles.length > 0 ? uploadedFiles[0].file : null;

      try {
        await dispatch(
          postMessage({
            customer_id: customerId,
            subject,
            message,
            sent_by_ifa: 1,
            isdocument_message: 0,
            file: fileToSend,
          }),
        ).then((res: any) => {
          if (!res.payload.isError) {
            notify(res.payload.message, "", "success");
            setTimeout(() => {
              handleClose();
              if (customerId) {
                dispatch(fetchCustomerMessages({ customerId }));
              }
            }, 1000);
          }
        });
      } catch (error: any) {
        notify(error, "", "error");
      } finally {
        setActionProcessing(false);
      }
    },
  });

  const handleClose = () => {
    formikData.resetForm();
    setRawFiles([]);
    setUploadedFiles([]);
    onClose();
  };

  const isLimitReached = uploadedFiles.length >= 3;
  const isScanning = uploadedFiles.some(
    (f) => f.status === "uploading" || f.status === "scanning",
  );

  return (
    <Dialog open={open} modalType="modal">
      <DialogSurface>
        <DialogBody>
          <DialogTitle
            action={
              <DialogTrigger action="close">
                <Button
                  appearance="subtle"
                  aria-label="close"
                  icon={<Dismiss24Regular onClick={handleClose} />}
                />
              </DialogTrigger>
            }
          >
            Compose Message
          </DialogTitle>
          <DialogContent className={styles.dialogContent}>
            <Field
              validationState={
                formikData.touched.subject && formikData.errors.subject
                  ? "error"
                  : "none"
              }
              validationMessage={`${(formikData.touched.subject && formikData?.errors?.subject) ||
                ""
                }`}
            >
              <Input
                placeholder="Subject"
                onFocus={() => formikData.setFieldTouched("subject")}
                type="text"
                name="subject"
                value={formikData.values.subject}
                onChange={formikData.handleChange}
              />
            </Field>
            <Field
              validationState={
                formikData.touched.message && formikData.errors.message
                  ? "error"
                  : "none"
              }
              validationMessage={`${(formikData.touched.message && formikData?.errors?.message) ||
                ""
                }`}
            >
              <Textarea
                placeholder="Message"
                name="message"
                rows={5}
                value={formikData.values.message}
                onChange={formikData.handleChange}
              />
            </Field>

            {/* Hidden file input with allowed type restrictions */}
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept={ALLOWED_FILE_EXTENSIONS}
              style={{ display: "none" }}
              onChange={handleFileInputChange}
            />

            {/* File upload status rows */}
            <DynamicDropzone
              multiple={true}
              externalFiles={rawFiles}
              onFilesChange={(files: any) => setUploadedFiles(files)}
              userId={userId}
            />

            <div
              style={{
                display: "flex",
                justifyContent: "flex-start",
                alignItems: "center",
                gap: 10,
                margin: "10px 0",
              }}
            >
              <Button
                appearance="primary"
                className="asc-button-primary"
                icon={<Send24Filled />}
                onClick={() => formikData.handleSubmit()}
                disabled={
                  actionProcessing ||
                  loading ||
                  isScanning ||
                  Object.keys(formikData.errors).length > 0
                }
              >
                {actionProcessing ? "Sending..." : isScanning ? "Scanning..." : "Send"}
              </Button>
              <Button
                appearance="outline"
                className="asc-button-outline"
                icon={<Attach20Regular />}
                onClick={() => fileInputRef.current?.click()}
                disabled={isLimitReached}
                title={
                  isLimitReached
                    ? "Maximum 3 files allowed"
                    : `Attach file (${ALLOWED_FILE_EXTENSIONS})`
                }
              >
                Attach
              </Button>
            </div>
          </DialogContent>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}

export default ComposeMessage;
